/**
 * Discussions Page
 *
 * Story FOS-4.2.6: Master Components & Pages
 * Task 6: Create DiscussionsPage
 *
 * Story BL-039.1: Wire real API calls to oracle-bridge.
 *
 * Lists governance discussions with filtering and creation.
 * Integrates DiscussionList and CreateDiscussionModal components.
 *
 * BL-029.4: Migrated from nanostores $user to useAuth().user.
 *
 * @module pages/Governance/DiscussionsPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@hello-world-dao/auth';
import {
  DiscussionList,
  CreateDiscussionModal,
  type DiscussionSummary,
  type CreateDiscussionData,
} from '../../components/governance';
import {
  listGovernanceDiscussions,
  createGovernanceDiscussion,
  mapToDiscussionSummary,
} from '../../services/discussionService';
import { serviceErrorMessage } from '../../utils/serviceErrorMessage';

/**
 * Discussions page component
 *
 * BL-427: Read-only mode for nonmembers. Anyone authenticated can browse the
 * list, but the "New Discussion" button is hidden unless the caller's
 * membership_status from the session is 'Active'. The roles[]-based
 * <ProtectedRoute requiredRole="member"> in router.tsx never gated anyone
 * because every authenticated user has roles=['member'] by default — see
 * BL-427 story for the full root-cause writeup.
 */
export function DiscussionsPage() {
  const navigate = useNavigate();
  const { user, membershipStatus } = useAuth();
  // Only Active SBT holders can create. The oracle-bridge POST /api/discussions
  // also enforces this server-side (load-bearing fix); this UI gate is just
  // UX so nonmembers don't see a button that would 403.
  const canCreate = membershipStatus === 'Active';

  const [discussions, setDiscussions] = useState<DiscussionSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load discussions on mount
  useEffect(() => {
    const loadDiscussions = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const govDiscussions = await listGovernanceDiscussions();
        setDiscussions(govDiscussions.map(mapToDiscussionSummary));
      } catch (err) {
        console.error('Failed to load discussions:', err);
        // bl-1153-1: prefer whatever the server actually said; this page's copy
        // remains the fallback for the no-server-text case.
        setError(
          serviceErrorMessage(err) ?? 'Failed to load discussions. Please try again later.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadDiscussions();
  }, []);

  // Handle discussion selection
  const handleSelect = useCallback(
    (discussionId: string) => {
      navigate(`/discussions/${discussionId}`);
    },
    [navigate]
  );

  // Handle create button click
  const handleCreateClick = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  // Handle create modal close
  const handleCreateClose = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  // Handle discussion creation
  const handleCreate = useCallback(
    async (data: CreateDiscussionData) => {
      try {
        const result = await createGovernanceDiscussion({
          title: data.title,
          category: data.category,
          description: '',
        });

        setIsCreateModalOpen(false);
        navigate(`/discussions/${result.discussionId}`);
      } catch (err) {
        console.error('Failed to create discussion:', err);
        throw err;
      }
    },
    [navigate]
  );

  // Get participating discussion IDs
  const participatingIds = discussions
    .filter((d) => d.proposerId === user?.userId)
    .map((d) => d.id);

  return (
    <div>
      {error && (
        <div className="text-destructive text-center py-4">{error}</div>
      )}

      <DiscussionList
        discussions={discussions}
        currentUserId={user?.userId}
        participatingDiscussionIds={participatingIds}
        isLoading={isLoading}
        onSelect={handleSelect}
        onCreate={canCreate ? handleCreateClick : undefined}
      />

      {canCreate && (
        <CreateDiscussionModal
          isOpen={isCreateModalOpen}
          onClose={handleCreateClose}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
}

export default DiscussionsPage;
